import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { FlaskConical, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { listTestStudents, setStudentEnvironment, purgeTestData } from "@/lib/studentsApi";

const fmt = (d) => (d ? new Date(d).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

export default function TestStudents() {
  const { user } = useAuth();
  const authed = !!user?.permissions?.manage;
  const [data, setData] = useState(null);
  const load = () => listTestStudents().then(setData).catch(() => toast.error("Could not load test students"));
  useEffect(() => { if (authed) load(); }, [authed]); // eslint-disable-line react-hooks/exhaustive-deps
  if (user && !authed) return <Navigate to="/" replace />;

  const purge = async () => {
    if (window.prompt(`This permanently deletes ${data.total} TEST students and their events/handoffs. PRODUCTION and review-flagged records are untouched. Type PURGE to confirm:`) !== "PURGE") return;
    try { const r = await purgeTestData(); toast.success(`Deleted ${r.deleted_students} test students, ${r.deleted_events} events`); load(); }
    catch { toast.error("Purge failed"); }
  };
  const reclassify = async (sid, environment) => {
    const reason = window.prompt(`Reason for marking ${sid} as ${environment}:`);
    if (!reason || reason.trim().length < 3) return;
    try { await setStudentEnvironment(sid, environment, reason.trim()); toast.success(`${sid} → ${environment}`); load(); }
    catch { toast.error("Could not update classification"); }
  };

  return (
    <div className="space-y-5" data-testid="test-students-page">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2"><FlaskConical className="h-6 w-6 text-amber-600" /> Test Students</h1>
        <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-mono font-semibold uppercase tracking-widest text-amber-800" data-testid="test-data-banner">
          TEST DATA — EXCLUDED FROM PRODUCTION METRICS
        </div>
        <p className="text-sm text-muted-foreground mt-2">Only records explicitly classified as TEST (X-EZ-Environment header, source.environment = TEST, reserved test domain, or manual). Nothing here counts toward Students, Daily Results, digests, AI-credit KPIs or Brain outcomes. Records are never deleted.</p>
      </div>
      {data && (
        <div className="flex flex-wrap gap-2 text-xs" data-testid="test-students-summary">
          <span className="rounded-full border bg-card px-2.5 py-1">Test students: <b data-testid="test-students-total">{data.total}</b></span>
          <span className="rounded-full border bg-card px-2.5 py-1">Production students: <b data-testid="production-students-total">{data.production_total}</b></span>
          <span className="rounded-full border bg-card px-2.5 py-1">Test activity (30d): <b>{data.test_events_30d}</b></span>
          <span className="rounded-full border bg-card px-2.5 py-1">Test emails (30d): <b>{data.test_emails_30d}</b></span>
          <button data-testid="purge-test-data" disabled={!data.total} onClick={purge} className="ml-auto rounded-md border border-rose-300 bg-rose-50 px-2.5 py-1 font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-40">Permanently delete all TEST records</button>
        </div>
      )}
      <div className="overflow-x-auto rounded-xl border bg-card">
        <table className="w-full text-xs" data-testid="test-students-table">
          <thead><tr className="text-left text-muted-foreground"><th className="p-2">Student</th><th className="p-2">Test email</th><th className="p-2">Created</th><th className="p-2">Test activity</th><th className="p-2">Status</th><th className="p-2">Classified because</th><th className="p-2"></th></tr></thead>
          <tbody>
            {(data?.items || []).map((s) => (
              <tr key={s.student_id} className="border-t" data-testid={`test-student-${s.student_id}`}>
                <td className="p-2"><div className="font-medium">{s.name}</div><div className="font-mono text-muted-foreground">{s.student_id}</div></td>
                <td className="p-2 font-mono">{s.email}</td>
                <td className="p-2">{fmt(s.created_at)}</td>
                <td className="p-2">{Object.entries(s.activity || {}).map(([k, v]) => `${k} ×${v}`).join(", ") || "—"}{s.handoffs ? ` · ${s.handoffs} handoff(s)` : ""}</td>
                <td className="p-2">{s.status}{s.ai?.status === "connected" ? " · AI connected" : ""}</td>
                <td className="p-2 text-muted-foreground">{s.test_reason || "manual"}{s.environment_set_by ? ` · by ${s.environment_set_by}` : ""}</td>
                <td className="p-2"><button data-testid={`mark-production-${s.student_id}`} onClick={() => reclassify(s.student_id, "PRODUCTION")} className="rounded-md border px-2 py-1 hover:bg-secondary">Mark PRODUCTION</button></td>
              </tr>
            ))}
            {data && data.items.length === 0 && <tr><td colSpan={7} className="p-4 text-center text-muted-foreground" data-testid="test-students-empty">No test students classified.</td></tr>}
          </tbody>
        </table>
      </div>
      {data?.needs_manual_review?.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="test-review-list">
          <div className="flex items-center gap-1.5 font-semibold mb-1"><AlertTriangle className="h-4 w-4" /> Possible test data — left as PRODUCTION, review manually</div>
          <ul className="space-y-1">
            {data.needs_manual_review.map((s) => (
              <li key={s.student_id} className="flex flex-wrap items-center gap-2"><span className="font-mono">{s.student_id}</span> {s.name} · {s.email} · {fmt(s.created_at)}
                <button data-testid={`mark-test-${s.student_id}`} onClick={() => reclassify(s.student_id, "TEST")} className="rounded-md border border-amber-300 px-2 py-0.5 hover:bg-amber-100">Mark TEST</button></li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
